import React, { useCallback, useEffect, useMemo, useState } from "react";
import JSZip from "jszip";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  Pets as PetsIcon,
  Palette as PaletteIcon,
  Refresh as RefreshIcon,
  RocketLaunch as RocketLaunchIcon,
  Checklist as ChecklistIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import MascotService from "../services/MascotService";
import type { App } from "../types/app";
import type {
  CreateMascotBaseVersionRequest,
  MascotBaseVersion,
  MascotCharacterProfile,
  MascotStateJob,
  MascotStatePrompt,
} from "../types/mascot";

const STATE_OPTIONS = [
  "idle",
  "loading",
  "in_progress",
  "success",
  "error",
  "empty",
  "thinking",
  "celebration",
  "warning",
  "offline",
];

const IMAGE_MODELS = [
  "gpt-image-2.5-flare",
  "gpt-image-2.5-sunburst",
  "gemini-2.5-flash-image",
];

function statusColor(status: string) {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "generating") return "warning";
  return "secondary";
}

function normalizeProfile(input: Partial<MascotCharacterProfile> | undefined): MascotCharacterProfile {
  return {
    name: String(input?.name || "Nova"),
    archetype: String(input?.archetype || "Friendly helper"),
    speciesOrForm: String(input?.speciesOrForm || "Stylized creature"),
    silhouette: String(input?.silhouette || "Rounded silhouette"),
    personality: String(input?.personality || "Playful"),
    colorPalette: String(input?.colorPalette || "Coral and teal"),
    wardrobeOrAccessories: String(input?.wardrobeOrAccessories || "Minimal badge"),
  };
}

export default function MascotCreator() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [baseVersions, setBaseVersions] = useState<MascotBaseVersion[]>([]);
  const [stateJobs, setStateJobs] = useState<MascotStateJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [baseForm, setBaseForm] = useState<CreateMascotBaseVersionRequest>({
    appId: "",
    creativeDirection: "",
    includeAppContext: true,
    aiMode: true,
  });

  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>(["idle", "success"]);
  const [statesDirection, setStatesDirection] = useState("");
  const [baseModel, setBaseModel] = useState(IMAGE_MODELS[0]);
  const [stateModel, setStateModel] = useState(IMAGE_MODELS[0]);
  const [creatingBase, setCreatingBase] = useState(false);
  const [generatingBaseVersionId, setGeneratingBaseVersionId] = useState("");
  const [creatingStatePlan, setCreatingStatePlan] = useState(false);
  const [generatingStateJobId, setGeneratingStateJobId] = useState("");
  const [baseDrafts, setBaseDrafts] = useState<
    Record<string, { characterProfile: MascotCharacterProfile; baseImagePrompt: string }>
  >({});
  const [stateDrafts, setStateDrafts] = useState<Record<string, MascotStatePrompt[]>>({});

  const filteredBaseVersions = useMemo(
    () => baseVersions.filter((item) => !baseForm.appId || item.appId === baseForm.appId),
    [baseForm.appId, baseVersions],
  );
  const filteredStateJobs = useMemo(
    () => stateJobs.filter((item) => !baseForm.appId || item.appId === baseForm.appId),
    [baseForm.appId, stateJobs],
  );

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const appData = await AppService.getUserApps(user.email);
    setApps(appData);
  }, [user?.email]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [versions, jobs] = await Promise.all([
        MascotService.listBaseVersions(baseForm.appId ? { appId: baseForm.appId } : undefined),
        MascotService.listStateJobs(baseForm.appId ? { appId: baseForm.appId } : undefined),
      ]);
      setBaseVersions(versions);
      setStateJobs(jobs);
      setBaseDrafts((prev) => {
        const next = { ...prev };
        versions.forEach((version) => {
          if (!next[version.versionId]) {
            next[version.versionId] = {
              characterProfile: normalizeProfile(version.characterProfile),
              baseImagePrompt: version.baseImagePrompt,
            };
          }
        });
        return next;
      });
      setStateDrafts((prev) => {
        const next = { ...prev };
        jobs.forEach((job) => {
          if (!next[job.jobId]) {
            next[job.jobId] = [...job.statePrompts];
          }
        });
        return next;
      });
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load mascot data.");
    } finally {
      setLoading(false);
    }
  }, [baseForm.appId]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedVersionId && filteredBaseVersions[0]?.versionId) {
      setSelectedVersionId(filteredBaseVersions[0].versionId);
    }
  }, [filteredBaseVersions, selectedVersionId]);

  const handleCreateBasePlan = async () => {
    setError(null);
    setSuccess(null);
    setCreatingBase(true);
    try {
      const response = await MascotService.createBaseVersion(baseForm);
      setSuccess(response.message || "Mascot base plan created.");
      setActiveTab(0);
      await loadData();
    } catch (createError: any) {
      setError(createError?.message || "Failed to create mascot base plan.");
    } finally {
      setCreatingBase(false);
    }
  };

  const handleGenerateBaseImage = async (version: MascotBaseVersion) => {
    const review = baseDrafts[version.versionId];
    if (!review) return;
    setError(null);
    setSuccess(null);
    setGeneratingBaseVersionId(version.versionId);
    try {
      const response = await MascotService.generateBaseImage({
        versionId: version.versionId,
        model: baseModel,
        characterProfile: review.characterProfile,
        baseImagePrompt: review.baseImagePrompt,
      });
      setSuccess(response.message || "Mascot base image generated.");
      await loadData();
    } catch (runError: any) {
      setError(runError?.message || "Failed to generate mascot base image.");
    } finally {
      setGeneratingBaseVersionId("");
    }
  };

  const handleCreateStatePlan = async () => {
    setError(null);
    setSuccess(null);
    if (!selectedVersionId) {
      setError("Select a mascot base version first.");
      return;
    }
    if (!selectedStates.length) {
      setError("Select at least one state.");
      return;
    }
    setCreatingStatePlan(true);
    try {
      const response = await MascotService.createStatePlan({
        versionId: selectedVersionId,
        states: selectedStates,
        creativeDirection: statesDirection,
      });
      setSuccess(response.message || "Mascot state prompts generated.");
      setActiveTab(1);
      await loadData();
    } catch (planError: any) {
      setError(planError?.message || "Failed to create mascot state prompts.");
    } finally {
      setCreatingStatePlan(false);
    }
  };

  const handleGenerateStateImages = async (job: MascotStateJob) => {
    const reviewPrompts = stateDrafts[job.jobId];
    if (!reviewPrompts || !reviewPrompts.length) return;
    setError(null);
    setSuccess(null);
    setGeneratingStateJobId(job.jobId);
    try {
      const response = await MascotService.generateStateImages({
        jobId: job.jobId,
        model: stateModel,
        statePrompts: reviewPrompts,
      });
      setSuccess(response.message || "Mascot states generated.");
      setActiveTab(2);
      await loadData();
    } catch (genError: any) {
      setError(genError?.message || "Failed to generate mascot states.");
    } finally {
      setGeneratingStateJobId("");
    }
  };

  const handleDownloadStateAssets = async (job: MascotStateJob) => {
    if (job.zipAsset?.downloadUrl) {
      window.open(job.zipAsset.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const downloadableAssets = job.stateAssets.filter((item) => item.asset?.downloadUrl);
    if (!downloadableAssets.length) {
      setError("No state assets found to download.");
      return;
    }

    try {
      setError(null);
      const zip = new JSZip();
      await Promise.all(
        downloadableAssets.map(async (item) => {
          const url = item.asset?.downloadUrl;
          if (!url) return;
          const response = await fetch(url);
          if (!response.ok) return;
          const blob = await response.blob();
          const stateFileName = String(item.state || "unknown")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
          zip.file(`${stateFileName || "unknown"}_state_mascot.png`, blob);
        }),
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const blobUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      const appSlug = String(job.appName || "app")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      link.href = blobUrl;
      link.download = `mascot_states_${appSlug || "app"}_${job.jobId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (downloadError: any) {
      setError(downloadError?.message || "Failed to create ZIP download.");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2.5 }}>
      <Card
        sx={{
          mb: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(255,244,229,0.9) 0%, rgba(232,245,255,0.9) 100%)",
        }}
      >
        <CardContent sx={{ pb: "16px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PetsIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Mascot Creator
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
            Build a reusable mascot system: base character, state prompts, then final PNG exports.
          </Typography>
          <Tabs sx={{ mt: 1.5 }} value={activeTab} onChange={(_, tab) => setActiveTab(tab)}>
            <Tab icon={<PaletteIcon />} iconPosition="start" label="1. Base Mascot" />
            <Tab icon={<ChecklistIcon />} iconPosition="start" label="2. State Prompts" />
            <Tab icon={<RocketLaunchIcon />} iconPosition="start" label="3. Generate States" />
          </Tabs>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2.5 }}>
        <CardContent sx={{ display: "grid", gap: 1.2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Registered App
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
              Refresh
            </Button>
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>Choose your app</InputLabel>
            <Select
              label="Choose your app"
              value={baseForm.appId}
              onChange={(event) =>
                setBaseForm((prev) => ({ ...prev, appId: String(event.target.value || "") }))
              }
            >
              <MenuItem value="">
                <em>Choose your app</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {activeTab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Card variant="outlined" sx={{ borderRadius: 2.5, height: "100%" }}>
              <CardContent sx={{ display: "grid", gap: 1.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Create Base Plan
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={baseForm.aiMode !== false}
                      onChange={(event) =>
                        setBaseForm((prev) => ({ ...prev, aiMode: event.target.checked }))
                      }
                    />
                  }
                  label="AI mode (generate profile + prompt)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={baseForm.includeAppContext !== false}
                      onChange={(event) =>
                        setBaseForm((prev) => ({
                          ...prev,
                          includeAppContext: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Use app context"
                />
                <TextField
                  size="small"
                  label="Custom direction (optional)"
                  multiline
                  minRows={4}
                  value={baseForm.creativeDirection || ""}
                  onChange={(event) =>
                    setBaseForm((prev) => ({
                      ...prev,
                      creativeDirection: event.target.value,
                    }))
                  }
                />
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleCreateBasePlan}
                  disabled={!baseForm.appId || creatingBase}
                >
                  {creatingBase ? "Creating..." : "Create Base Plan"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={8}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Base Versions
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Base Image Model</InputLabel>
                    <Select
                      label="Base Image Model"
                      value={baseModel}
                      onChange={(event) => setBaseModel(String(event.target.value))}
                    >
                      {IMAGE_MODELS.map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {filteredBaseVersions.length === 0 ? (
                  <Typography color="text.secondary">No mascot versions yet.</Typography>
                ) : (
                  <Box sx={{ display: "grid", gap: 1.2 }}>
                    {filteredBaseVersions.map((version) => {
                      const draft = baseDrafts[version.versionId];
                      return (
                        <Paper key={version.versionId} variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                            <Typography sx={{ fontWeight: 700 }}>
                              v{version.versionNumber} • {version.appName || "App"}
                            </Typography>
                            <Chip size="small" color={statusColor(version.status) as any} label={version.status} />
                          </Box>
                          {draft && (
                            <Grid container spacing={1}>
                              {(
                                [
                                  "name",
                                  "archetype",
                                  "speciesOrForm",
                                  "silhouette",
                                  "personality",
                                  "colorPalette",
                                  "wardrobeOrAccessories",
                                ] as const
                              ).map((key) => (
                                <Grid item xs={12} md={6} key={`${version.versionId}_${key}`}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label={key}
                                    value={draft.characterProfile[key]}
                                    onChange={(event) =>
                                      setBaseDrafts((prev) => ({
                                        ...prev,
                                        [version.versionId]: {
                                          ...draft,
                                          characterProfile: {
                                            ...draft.characterProfile,
                                            [key]: event.target.value,
                                          },
                                        },
                                      }))
                                    }
                                  />
                                </Grid>
                              ))}
                              <Grid item xs={12}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={4}
                                  label="Base mascot image prompt"
                                  value={draft.baseImagePrompt}
                                  onChange={(event) =>
                                    setBaseDrafts((prev) => ({
                                      ...prev,
                                      [version.versionId]: {
                                        ...draft,
                                        baseImagePrompt: event.target.value,
                                      },
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleGenerateBaseImage(version)}
                                    disabled={generatingBaseVersionId === version.versionId}
                                  >
                                    {generatingBaseVersionId === version.versionId
                                      ? "Generating..."
                                      : "Generate Base Mascot"}
                                  </Button>
                                  {version.baseAsset?.downloadUrl && (
                                    <Button
                                      size="small"
                                      onClick={() => setPreviewImage(version.baseAsset?.downloadUrl || null)}
                                    >
                                      Preview
                                    </Button>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent sx={{ display: "grid", gap: 1.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Build State Prompt Set
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Base mascot version</InputLabel>
                  <Select
                    label="Base mascot version"
                    value={selectedVersionId}
                    onChange={(event) => setSelectedVersionId(String(event.target.value))}
                  >
                    {filteredBaseVersions.map((version) => (
                      <MenuItem key={version.versionId} value={version.versionId}>
                        v{version.versionNumber} - {version.status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>States</InputLabel>
                  <Select
                    label="States"
                    multiple
                    value={selectedStates}
                    onChange={(event) =>
                      setSelectedStates(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(","),
                      )
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} size="small" label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {STATE_OPTIONS.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="State generation direction"
                  multiline
                  minRows={3}
                  value={statesDirection}
                  onChange={(event) => setStatesDirection(event.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateStatePlan}
                  disabled={!selectedVersionId || creatingStatePlan}
                >
                  {creatingStatePlan ? "Generating prompts..." : "Generate State Prompts"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={8}>
            <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    State Prompt Review
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Edit prompts before stage 3 generation.
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gap: 1.2 }}>
                  {filteredStateJobs
                    .filter((job) => job.status === "review_pending")
                    .map((job) => {
                      const prompts = stateDrafts[job.jobId] || [];
                      return (
                        <Paper key={job.jobId} variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography sx={{ fontWeight: 700 }}>State job {job.jobId.slice(0, 8)}</Typography>
                            <Chip size="small" color={statusColor(job.status) as any} label={job.status} />
                          </Box>
                          <Box sx={{ display: "grid", gap: 1 }}>
                            {prompts.map((item, index) => (
                              <TextField
                                key={`${job.jobId}_${item.state}`}
                                size="small"
                                label={`${item.state} prompt`}
                                multiline
                                minRows={3}
                                value={item.prompt}
                                onChange={(event) =>
                                  setStateDrafts((prev) => {
                                    const next = [...prompts];
                                    next[index] = { ...next[index], prompt: event.target.value };
                                    return { ...prev, [job.jobId]: next };
                                  })
                                }
                              />
                            ))}
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RocketLaunchIcon />}
                              onClick={() => setActiveTab(2)}
                            >
                              Go to Generate Assets Stage
                            </Button>
                          </Box>
                        </Paper>
                      );
                    })}
                  {!filteredStateJobs.some((job) => job.status === "review_pending") && (
                    <Typography color="text.secondary">No review-pending state plans yet.</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Generate & Export Mascot States
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Generation Model</InputLabel>
                <Select
                  label="Generation Model"
                  value={stateModel}
                  onChange={(event) => setStateModel(String(event.target.value))}
                >
                  {IMAGE_MODELS.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "grid", gap: 1.2 }}>
              {filteredStateJobs.map((job) => (
                <Paper key={job.jobId} variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {job.appName || "App"} • {job.inputs.states.length} states
                    </Typography>
                    <Chip size="small" color={statusColor(job.status) as any} label={job.status} />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    {job.inputs.states.map((state) => (
                      <Chip key={`${job.jobId}_${state}`} size="small" variant="outlined" label={state} />
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleGenerateStateImages(job)}
                    disabled={
                      job.status !== "review_pending" ||
                      generatingStateJobId === job.jobId
                    }
                  >
                    {generatingStateJobId === job.jobId ? "Generating..." : "Generate State PNGs"}
                  </Button>
                  {(Boolean(job.zipAsset?.downloadUrl) || job.stateAssets.length > 0) && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => handleDownloadStateAssets(job)}
                    >
                      Download Assets ZIP
                    </Button>
                  )}
                  {job.status === "completed" && job.stateAssets.length > 0 && (
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {job.stateAssets.map((asset) => (
                        <Grid item xs={6} md={3} key={`${job.jobId}_${asset.state}`}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 0.8, borderRadius: 1.5, cursor: "pointer" }}
                            onClick={() => setPreviewImage(asset.asset?.downloadUrl || null)}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {asset.state}
                            </Typography>
                            {asset.asset?.downloadUrl ? (
                              <Box
                                component="img"
                                src={asset.asset.downloadUrl}
                                alt={asset.state}
                                sx={{ width: "100%", height: 120, objectFit: "contain", mt: 0.4 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Missing image
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              ))}
              {!filteredStateJobs.length && (
                <Typography color="text.secondary">No mascot state jobs yet.</Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
        <DialogTitle>Mascot Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Mascot Preview"
              sx={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
